import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Box,
  Fab,
  Paper,
  Typography,
  TextField,
  IconButton,
  Avatar,
  CircularProgress,
  Grow,
  InputAdornment,
} from '@mui/material';
import { SmartToyIcon, CloseIcon, SendIcon } from '@/components/common/icons';
import { chatbotService } from '@/services/chatbot.service';
import { chatbotStyles } from './chatbot.styles';

interface ChatMessage {
  text: string;
  isUser: boolean;
}

const WELCOME_MESSAGE = 'Hello! I\'m CentraJob Assistant. How can I help you today?';

const ChatBot: React.FC = () => {
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([
    { text: WELCOME_MESSAGE, isUser: false },
  ]);
  const [inputValue, setInputValue] = useState('');
  const [isBotTyping, setIsBotTyping] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, isBotTyping, scrollToBottom]);

  const handleSend = async () => {
    const trimmed = inputValue.trim();
    if (!trimmed || isBotTyping) return;

    const userMessage: ChatMessage = { text: trimmed, isUser: true };
    setMessages((prev) => [...prev, userMessage]);
    setInputValue('');
    setIsBotTyping(true);

    try {
      const reply = await chatbotService.sendMessage(trimmed);
      setMessages((prev) => [...prev, { text: reply, isUser: false }]);
    } catch {
      setMessages((prev) => [
        ...prev,
        { text: 'Failed to connect. Please try again later.', isUser: false },
      ]);
    } finally {
      setIsBotTyping(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <Box sx={chatbotStyles.container}>
      {/* Chat Window */}
      <Grow in={isOpen} unmountOnExit>
        <Paper elevation={8} sx={chatbotStyles.chatWindow}>
          {/* Header */}
          <Box sx={chatbotStyles.header}>
            <Avatar sx={chatbotStyles.headerAvatar}>
              <SmartToyIcon sx={chatbotStyles.headerAvatarIcon} />
            </Avatar>
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle2" fontWeight={700} sx={chatbotStyles.headerTitle}>
                Chat with us
              </Typography>
              <Typography variant="caption" sx={chatbotStyles.headerSubtitle}>
                CentraJob Support
              </Typography>
            </Box>
            <IconButton size="small" onClick={() => setIsOpen(false)} sx={chatbotStyles.closeButton}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          {/* Messages */}
          <Box sx={chatbotStyles.messagesArea}>
            {messages.map((msg, index) => (
              <Box key={index} sx={chatbotStyles.messageRow(msg.isUser)}>
                <Box sx={chatbotStyles.messageBubble(msg.isUser)}>
                  {msg.text}
                </Box>
              </Box>
            ))}

            {/* Typing indicator */}
            {isBotTyping && (
              <Box sx={chatbotStyles.typingIndicator}>
                <CircularProgress size={14} sx={chatbotStyles.typingSpinner} />
                <Typography variant="caption" sx={chatbotStyles.typingText}>
                  CJ is typing...
                </Typography>
              </Box>
            )}
            <div ref={messagesEndRef} />
          </Box>

          {/* Input */}
          <Box sx={chatbotStyles.inputArea}>
            <TextField
              fullWidth
              size="small"
              placeholder="Type your question..."
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={isBotTyping}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={handleSend}
                      disabled={!inputValue.trim() || isBotTyping}
                      sx={chatbotStyles.sendButton}
                    >
                      <SendIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
                sx: chatbotStyles.inputField,
              }}
            />
          </Box>
        </Paper>
      </Grow>

      {/* Floating Action Button */}
      <Fab
        color="primary"
        onClick={() => setIsOpen((prev) => !prev)}
        sx={chatbotStyles.fab(isOpen)}
      >
        {isOpen ? <CloseIcon /> : <SmartToyIcon className="chat-icon" />}
      </Fab>
    </Box>
  );
};

export default ChatBot;
